import type { ReactNode } from 'react';
import { PaymentMethodEnum } from '@/types/common';
import { BlikWordmark } from './wordmarks/BlikWordmark';
import { PixWordmark } from './wordmarks/PixWordmark';

export interface BrandedCtaVisual {
  /** Solid button background color (brand-locked hex). */
  backgroundColor: string;
  /** Foreground color for the "Pay with" text and (via `currentColor`) the wordmark. */
  foregroundColor: string;
  /** SVG wordmark for the APM — expected to paint via `currentColor` for the letters. */
  wordmark: ReactNode;
  /**
   * Optional override for the "Pay with" label. Defaults to `t('payWith')`
   * ("Pay with"). Kept as an escape hatch — e.g. an APM whose CTA copy
   * differs from the standard "Pay with X".
   */
  labelKey?: string;
}

/**
 * Per-payment-method visual config for the branded APM CTA rendered in
 * templates that opt into `features.preferExpressButton` and don't have a
 * Stripe-hosted Express Checkout Element (i.e. BLIK, PIX). Add a row here
 * to enroll a new APM — the shared {@link BrandedPayCTA} component handles
 * the button chrome (size, disabled/loader states, idle pulse) so consumers
 * only supply brand identity.
 *
 * Amazon Pay is intentionally NOT here — its branded button comes from
 * Stripe's ECE (see {@link AmazonPayStripeForm}). This map is for the APMs
 * where Stripe does NOT provide an express iframe and we must paint the
 * branded CTA ourselves.
 */
export const BRANDED_PAY_CTA_CONFIG: Partial<Record<PaymentMethodEnum, BrandedCtaVisual>> = {
  [PaymentMethodEnum.BLIK]: {
    backgroundColor: '#000000',
    foregroundColor: '#FFFFFF',
    wordmark: <BlikWordmark />,
  },
  [PaymentMethodEnum.PIX]: {
    backgroundColor: '#32BCAD',
    foregroundColor: '#FFFFFF',
    wordmark: <PixWordmark />,
  },
};

/** True if the payment method has a branded-CTA visual registered. */
export const hasBrandedPayCta = (method: PaymentMethodEnum | undefined): method is PaymentMethodEnum =>
  method !== undefined && method in BRANDED_PAY_CTA_CONFIG;
